import { useState } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import RefreshIcon from '@mui/icons-material/Refresh'
import Refreshing from 'components/core/Refreshing'
import { WINDOWS } from '../StreamIngest/vocabulary'
import {
  FlowLayout,
  accountFrom,
  accountParams,
  useScrollToHash,
  withAccount,
} from '../Flow/parts'
import { useLoopReads } from '../Flow/reads'
import LoopPanel from '../Flow/LoopPanel'
import Delivery from './Delivery'
import Pushes from './Pushes'
import Listens from './Listens'
import Friends from './Friends'

// The Loop page: what happens to a quote once it is cut — the push about it,
// the visit that push began, whether anybody heard it, and whether it reached
// a friend. It opens with the loop's row of the Overview (`Flow/LoopPanel`),
// then a tab per question underneath.
//
// The page reads the loop once (`Flow/reads.js`) and hands the answers to the
// panel and to every tab, so nothing is read twice; the event table's read
// lands apart, so one it refuses leaves the rest standing. `?tab=` names the
// tab, so a link opens it. Tabs not built yet are drawn disabled, so the
// page's shape is visible before its numbers.
//
// The loop's reads are the fleet's and cannot be narrowed to one account.
// A link from an Overview on one account (`?user=…&name=…`) says so, and the
// page keeps the account on its tabs and its links, so the way back is still
// that account's.
//
// - Pushes: what each kind of push led to, the tab's point, then how they
//   were delivered.
// - Listens: who came back to hear something, how far into a quote they
//   get, and whether it is becoming a habit. Its own read, made while shown.
// - Friends: who hears whom, and whether being connected, and being heard,
//   goes with listening. Read with the page, since the panel's Friends stage
//   draws it too.
const PushesTab = ({ reads }) => (
  <>
    <Pushes
      answer={reads.pushes}
      error={reads.pushesError}
      loading={reads.pushesLoading}
    />
    <Delivery
      answer={reads.delivery}
      error={reads.deliveryError}
      loading={reads.loading}
    />
  </>
)

const TABS = [
  { key: 'pushes', label: 'Pushes', Component: PushesTab },
  { key: 'listens', label: 'Listens', Component: Listens },
  { key: 'friends', label: 'Friends', Component: Friends },
]

const Loop = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const asked = TABS.find(
    ({ key, Component }) => key === searchParams.get('tab') && Component
  )
  const tab = asked || TABS[0]
  const account = accountFrom(searchParams)
  const [windowDays, setWindowDays] = useState(7)
  const reads = useLoopReads({ days: windowDays })
  // A stage's link names a section (`#delivery`, `#network`): scrolled to
  // once the reads that draw it and everything above it have landed, or a
  // late one pushes the section back down.
  useScrollToHash(
    Boolean(reads.delivery) && !reads.pushesLoading && !reads.friendsLoading
  )
  // The page's refresh, which the open tab's own read follows.
  const [refreshKey, setRefreshKey] = useState(0)
  const { Component } = tab

  return (
    <Box
      sx={{
        px: { mobile: 2, desktop: 6 },
        py: { mobile: 2, desktop: 4 },
        // Fills its column: the layout would shrink it to its content, and
        // the flow turns on the room it is given.
        width: '100%',
        maxWidth: 1600,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Loop
        </Typography>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          value={windowDays}
          exclusive
          size="small"
          onChange={(event, next) => next && setWindowDays(next)}
        >
          {WINDOWS.map(({ days: value, label, title }) => (
            <ToggleButton key={value} value={value} title={title}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={() => {
              reads.refresh()
              setRefreshKey((n) => n + 1)
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The loop’s row of the{' '}
        <Link component={RouterLink} to={withAccount('/overview', account)}>
          Overview
        </Link>
        , then a tab per question · quotes → people, per UTC day · the event
        table starts on Oct 27, 2026
        {account
          ? ` · the whole fleet: the loop can’t be narrowed to ${
              account.username || account.id
            } yet`
          : ''}
      </Typography>

      <Refreshing active={reads.loading}>
        <FlowLayout>
          <LoopPanel reads={reads} user={account} to={null} />
        </FlowLayout>
      </Refreshing>

      <Tabs
        value={tab.key}
        onChange={(event, next) =>
          setSearchParams({ tab: next, ...accountParams(account) })
        }
        sx={{ mb: 3 }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        {TABS.map(({ key, label, Component: built }) => (
          <Tab key={key} value={key} label={label} disabled={!built} />
        ))}
      </Tabs>

      <Component reads={reads} days={windowDays} refreshKey={refreshKey} />
    </Box>
  )
}

export default Loop
